// The provinces and territories, the time zone each one keeps, and the reverse guess from a
// zone to the province it is almost certainly in.
//
// The province is the one setting: it decides which statutory calendar the board marks AND
// which time zone the company's schedule days are counted in. There used to be a separate
// time zone picker beside it, which only ever offered one sensible answer per province and
// could be set to disagree with it.
//
// Here rather than under server/ for the same reason scope.js is: the server validates the
// setting and derives the zone, the browser draws the picker and shows the zone, and two
// hand-kept copies of a list is one copy plus a latent bug. No imports and no I/O, so it
// obeys the same rule scope.js does.

export const PROVINCES = [
  ['BC', 'British Columbia'],
  ['AB', 'Alberta'],
  ['SK', 'Saskatchewan'],
  ['MB', 'Manitoba'],
  ['ON', 'Ontario'],
  ['QC', 'Quebec'],
  ['NB', 'New Brunswick'],
  ['NS', 'Nova Scotia'],
  ['PE', 'Prince Edward Island'],
  ['NL', 'Newfoundland and Labrador'],
  ['YT', 'Yukon'],
  ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'],
]

export const PROVINCE_IDS = new Set(PROVINCES.map(([id]) => id))

/**
 * The time zone a company in this province keeps — the one "today" is counted in.
 *
 * One per province. For most that is simply true; for the few that span more than one
 * (north-east BC and the Kootenays, north-west Ontario, Lloydminster, Labrador, Nunavut) it
 * is the zone most of the province keeps, and what differs is the hour either side of
 * midnight that decides which day is today. A company already saved with a different zone
 * keeps it until somebody changes the province — see updateSettings in repos/tenants.js.
 *
 * Canonical tzdata names only. America/Yellowknife and America/Montreal still resolve, but
 * as links to Edmonton and Toronto, and a link is a name the database is free to retire.
 */
export const TIMEZONE_FOR_PROVINCE = {
  BC: 'America/Vancouver',
  AB: 'America/Edmonton',
  SK: 'America/Regina',
  MB: 'America/Winnipeg',
  ON: 'America/Toronto',
  QC: 'America/Toronto',
  NB: 'America/Moncton',
  NS: 'America/Halifax',
  PE: 'America/Halifax',
  NL: 'America/St_Johns',
  YT: 'America/Whitehorse',
  NT: 'America/Edmonton',
  NU: 'America/Iqaluit',
}

export const timezoneForProvince = (province) => TIMEZONE_FOR_PROVINCE[province] || null

/**
 * The province a company in this zone is almost certainly in — the guess, for the one place
 * a zone is all there is: a tenant created from the command line with --timezone rather than
 * --province. Where a zone is shared (Toronto, Halifax, Edmonton) this names the province
 * most likely to be meant, so it is not the inverse of the map above and is not tested as one.
 *
 * The first seven are mirrored as inline SQL in migrations/016_statutory_holidays.sql, which
 * backfilled existing companies from them. That migration has run; it does not need the rest.
 */
export const PROVINCE_FOR_TIMEZONE = {
  'America/Vancouver': 'BC',
  'America/Edmonton': 'AB',
  'America/Regina': 'SK',
  'America/Winnipeg': 'MB',
  'America/Toronto': 'ON',
  'America/Halifax': 'NS',
  'America/St_Johns': 'NL',
  'America/Moncton': 'NB',
  'America/Whitehorse': 'YT',
  'America/Iqaluit': 'NU',
}

export const provinceForTimezone = (tz) => PROVINCE_FOR_TIMEZONE[tz] || null

/** null is a real value here: it means "do not mark holidays". */
export const isProvince = (v) => v === null || PROVINCE_IDS.has(v)
